// Редът с филтрите на „Натовареност" — СЪЩИЯТ скелет като „Продажби по
// периоди": два сегмента (Групирано / Във времето → Часове / Дни от
// седмицата / Месеци…) и чипове, които стесняват отговора на BE-то. Думите и
// класовете са същите нарочно — човек свиква с една справка и знае другата.
//
// Чиповете тук са два: статус на резервацията и маса от схемата.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useRestaurantSchema } from "../../../../hooks/useRestaurantSchema";
import {
  SALES_BY_PERIODS_UNITS,
  type SalesByPeriodsMode,
  type SalesByPeriodsUnit,
} from "../../../../api/reportsSalesByPeriods";
import { FilterChip, type FilterChipOption } from "../salesByPeriods/FilterChip";
import { RESERVATION_STATUSES, statusLabel } from "./reservationReportFormat";
import {
  EMPTY_OCCUPANCY_FILTERS,
  hasActiveOccupancyFilters,
  type OccupancyFilterState,
} from "./occupancyFilterState";

type Props = {
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
  onModeChange: (mode: SalesByPeriodsMode) => void;
  onUnitChange: (unit: SalesByPeriodsUnit) => void;
  filters: OccupancyFilterState;
  onFiltersChange: (filters: OccupancyFilterState) => void;
};

const segmentClass = (active: boolean) =>
  `inline-flex h-[28px] items-center whitespace-nowrap rounded-[7px] px-3 text-[11.5px] font-semibold leading-none transition-colors ${
    active
      ? "bg-brand-tint text-brand-deep-ink shadow-[inset_0_0_0_1px_var(--color-brand-tint2)]"
      : "text-body hover:bg-canvas hover:text-ink"
  }`;

const SEGMENT_SHELL =
  "inline-flex h-9 items-center gap-0.5 rounded-[10px] border border-shade-3 bg-surface p-[3px] shadow-[0_1px_2px_rgba(17,17,17,0.04)]";

const MODES: SalesByPeriodsMode[] = ["grouped", "timeline"];

export const ReservationsOccupancyFilters = ({
  mode,
  unit,
  onModeChange,
  onUnitChange,
  filters,
  onFiltersChange,
}: Props) => {
  const { t } = useTranslation();
  const { data: regions = [] } = useRestaurantSchema();

  const statusOptions = useMemo<FilterChipOption[]>(
    () =>
      RESERVATION_STATUSES.map((status) => ({
        value: status,
        label: statusLabel(t, status),
      })),
    [t],
  );

  // Масите от всички региони, по име; регионът е в скоби, когато са повече
  // от един — „5 (Тераса)" се различава от „5 (Зала)" само така.
  const tableOptions = useMemo<FilterChipOption[]>(() => {
    const manyRegions = regions.length > 1;
    return regions
      .flatMap((region) =>
        region.elements
          .filter((element) => element.kind === "table" && element.name)
          .map((element) => ({
            value: String(element.id),
            label: manyRegions ? `${element.name} (${region.name})` : element.name ?? "",
          })),
      )
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [regions]);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <div className={SEGMENT_SHELL} role="group">
        {MODES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={mode === candidate}
            onClick={() => onModeChange(candidate)}
            className={segmentClass(mode === candidate)}
          >
            {t(`reportsPage.salesPeriodsMode_${candidate}`)}
          </button>
        ))}
      </div>

      <div className={SEGMENT_SHELL} role="group">
        {SALES_BY_PERIODS_UNITS[mode].map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={unit === candidate}
            onClick={() => onUnitChange(candidate)}
            className={segmentClass(unit === candidate)}
          >
            {t(`reportsPage.salesPeriodsUnit_${candidate}`)}
          </button>
        ))}
      </div>

      <span className="hidden h-5 w-px bg-shade-2 sm:block" />

      <FilterChip
        allLabel={t("reportsPage.rvAllStatuses")}
        label={t("reportsPage.rvStatuses")}
        options={statusOptions}
        selected={filters.statuses}
        onChange={(statuses) => onFiltersChange({ ...filters, statuses })}
      />
      <FilterChip
        allLabel={t("reportsPage.rvAllTables")}
        label={t("reportsPage.rvColumnTables")}
        options={tableOptions}
        selected={filters.tableIds}
        onChange={(tableIds) => onFiltersChange({ ...filters, tableIds })}
      />

      {hasActiveOccupancyFilters(filters) ? (
        <button
          type="button"
          onClick={() => onFiltersChange(EMPTY_OCCUPANCY_FILTERS)}
          className="h-9 rounded-lg px-2.5 text-[12px] font-bold text-muted transition-colors hover:bg-shade-1 hover:text-ink"
        >
          {t("reportsPage.salesPeriodsClearFilters")}
        </button>
      ) : null}
    </div>
  );
};
